import { UNSAFE_Icon, MdArrowForward, MdOpenInNew } from '@dsx-react/icon';
import { classNames } from '@dsx-react/util';
import { useContext, createContext, forwardRef } from 'react';
import { jsx, jsxs } from 'react/jsx-runtime';

function styleInject(css, ref) {
  if (ref === void 0) ref = {};
  var insertAt = ref.insertAt;
  if (!css || typeof document === 'undefined') {
    return;
  }
  var head = document.head || document.getElementsByTagName('head')[0];
  var style = document.createElement('style');
  style.type = 'text/css';
  if (insertAt === 'top') {
    if (head.firstChild) {
      head.insertBefore(style, head.firstChild);
    } else {
      head.appendChild(style);
    }
  } else {
    head.appendChild(style);
  }
  if (style.styleSheet) {
    style.styleSheet.cssText = css;
  } else {
    style.appendChild(document.createTextNode(css));
  }
}

var css_248z = ".Link-module_link__czQ1j {\n  display: inline-block;\n  border-radius: 4px;\n  outline-offset: 2px;\n  -webkit-text-decoration: none;\n  text-decoration: none;\n}\n.Link-module_link__czQ1j:active {\n  color: var(--color-blue-600);\n  -webkit-text-decoration: underline;\n  text-decoration: underline;\n}\n.Link-module_link__czQ1j:hover {\n  -webkit-text-decoration: underline;\n  text-decoration: underline;\n}\n.Link-module_link__czQ1j:focus-visible {\n  outline: 2px solid var(--color-blue-500);\n  -webkit-text-decoration: none;\n  text-decoration: none;\n}\n.Link-module_link__czQ1j:hover {\n  color: var(--color-blue-600);\n}\n.Link-module_link__czQ1j:active {\n  color: var(--color-blue-700);\n}\n\n.Link-module_variant-default__ICFEV, .Link-module_variant-default__ICFEV:visited {\n  color: var(--color-blue-500);\n}\n\n.Link-module_variant-subtle__UYPes, .Link-module_variant-subtle__UYPes:visited {\n  color: var(--color-neutral-700);\n}\n\n.Link-module_variant-inverse__A2hhb, .Link-module_variant-inverse__A2hhb:visited {\n  color: var(--color-neutral-0-static);\n}\n.Link-module_variant-inverse__A2hhb:focus-visible {\n  outline: 2px solid var(--color-neutral-0-static);\n  -webkit-text-decoration: none;\n  text-decoration: none;\n}\n\n.Link-module_link__czQ1j.Link-module_size-default__piztD > svg {\n  fill: currentColor;\n  height: 1.5em;\n  width: 1.5em;\n  vertical-align: -0.25em;\n  margin-top: -0.125em;\n  margin-bottom: -0.125em;\n}\n\n.Link-module_link__czQ1j.Link-module_size-small__6N9dc > svg {\n  fill: currentColor;\n  height: 1em;\n  width: 1em;\n  vertical-align: 0;\n  margin-top: -0.125em;\n  margin-bottom: -0.125em;\n}\n\n.Link-module_size-default__piztD {\n  font-weight: var(--UNSAFE-body-font-weight);\n  font-size: var(--UNSAFE-body-font-size);\n  line-height: var(--UNSAFE-body-line-height);\n  letter-spacing: var(--UNSAFE-body-letter-spacing);\n}\n\n.Link-module_size-small__6N9dc {\n  font-weight: var(--UNSAFE-body-small-font-weight);\n  font-size: var(--UNSAFE-body-small-font-size);\n  line-height: var(--UNSAFE-body-small-line-height);\n  letter-spacing: var(--UNSAFE-body-small-letter-spacing);\n}\n\n.Link-module_link-style-standalone__bDix4 {\n  font-weight: 500;\n}";
var styles = {"link":"Link-module_link__czQ1j","variant-default":"Link-module_variant-default__ICFEV","variant-subtle":"Link-module_variant-subtle__UYPes","variant-inverse":"Link-module_variant-inverse__A2hhb","size-default":"Link-module_size-default__piztD","size-small":"Link-module_size-small__6N9dc","link-style-standalone":"Link-module_link-style-standalone__bDix4"};
styleInject(css_248z);

const LinkSourceContext = /*#__PURE__*/createContext({});
const defaultLinkSources = {
  default: /*#__PURE__*/forwardRef((props, ref) => /*#__PURE__*/jsx("a", {
    ref: ref,
    ...props
  })),
  none: /*#__PURE__*/forwardRef((props, ref) => /*#__PURE__*/jsx("span", {
    ref: ref,
    ...props
  }))
};
function LinkSourceProvider(props) {
  const {
    linkSources = defaultLinkSources,
    children
  } = props;
  return /*#__PURE__*/jsx(LinkSourceContext.Provider, {
    value: linkSources,
    children: children
  });
}
function useLinkSource(props) {
  const {
    href,
    hrefType,
    target,
    rel,
    ...rest
  } = props;
  const record = useContext(LinkSourceContext);
  let Link = record[hrefType || 'default'];
  if (!Link) {
    console.warn(`LinkProvider: tried to get the link source "${hrefType}", but it was not found. Is this source listed in the top-level Provider.listSources of your application? Falling back to "default".`);
    Link = record['default'];
  }
  return {
    Link,
    linkProps: {
      href,
      target,
      rel
    },
    rest
  };
}

const actionIndicators = {
  MdArrowForward,
  MdOpenInNew
};
const Link = /*#__PURE__*/forwardRef(function Link(props, ref) {
  const {
    Link: ResolvedLink,
    linkProps,
    rest
  } = useLinkSource(props);
  const {
    className: callerClassName,
    tag,
    children,
    icon,
    actionIndicator,
    variant = 'default',
    linkStyle = 'inline',
    size = 'default',
    ...dataProps
  } = rest;
  const className = classNames('dsx-component', styles.link, styles[`variant-${variant}`], styles[`link-style-${linkStyle}`], styles[`size-${size}`], callerClassName);
  return /*#__PURE__*/jsxs(ResolvedLink, {
    ...dataProps,
    ...linkProps,
    className: className,
    ref: ref,
    "data-dsx--link": tag,
    children: [icon && /*#__PURE__*/jsx(UNSAFE_Icon, {
      icon: icon
    }), children, actionIndicator && /*#__PURE__*/jsx(UNSAFE_Icon, {
      icon: actionIndicators[actionIndicator]
    })]
  });
});

export { Link, LinkSourceProvider, useLinkSource };
